import React, { useState, useRef, useEffect } from "react";
import { authHeaders } from "../auth";
import "./InputBar.css";

const QUICK_ACTIONS = [
  "Admission snapshot",
  "Top 10 leads by score",
  "Revenue this month",
  "Seat fill rate by course",
  "Lead conversion rate",
];

const API_BASE = process.env.REACT_APP_API_BASE;

// Voice input is only available where the browser exposes microphone capture.
const VOICE_SUPPORTED =
  typeof navigator !== "undefined" &&
  navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function" &&
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined";

// Text-like documents we can read client-side and attach as context.
const TEXT_EXTENSIONS = [".txt", ".md", ".csv", ".json"];
const MAX_ATTACH_CHARS = 12000; // trim very large docs so the prompt stays sane.

function InputBar({
  onSend,
  disabled,
  showQuickActions = false,
  onRecordingChange,
  onOpenSettings,
  onOpenVoice,
}) {
  const [input, setInput] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [attachment, setAttachment] = useState(null); // { name, content }
  const [attachNote, setAttachNote] = useState("");
  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  // Hands-free voice: silence detection + auto-send.
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const autoSendRef = useRef(false);

  // Surface recording state to the parent (drives the header logo animation).
  useEffect(() => {
    if (onRecordingChange) onRecordingChange(recording);
  }, [recording, onRecordingChange]);

  const buildPayload = (text) => {
    if (attachment && attachment.content) {
      return `Attached document: ${attachment.name}\n"""\n${attachment.content}\n"""\n\nQuestion: ${text}`;
    }
    return text;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !disabled) {
      onSend(buildPayload(input.trim()));
      setInput("");
      setAttachment(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 150) + "px";
  };

  /* ── Attachment handling ─────────────────────────────────────────────── */
  const openFilePicker = () => {
    if (disabled || recording || transcribing) return;
    setAttachNote("");
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset so selecting the same file again re-fires the change event.
    e.target.value = "";
    if (!file) return;

    const lower = file.name.toLowerCase();
    const isExcel = lower.endsWith(".xlsx") || lower.endsWith(".xls");
    const isText = TEXT_EXTENSIONS.some((ext) => lower.endsWith(ext));

    // Excel is binary — upload to the backend to parse it into rows/text context.
    if (isExcel) {
      setAttachNote("Reading spreadsheet…");
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API_BASE}/files/upload`, {
          method: "POST", headers: { ...authHeaders() }, body: fd,
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        let content = data.text || "";
        if (content.length > MAX_ATTACH_CHARS) content = content.slice(0, MAX_ATTACH_CHARS) + "\n…[truncated]";
        setAttachment({ name: file.name, content });
        setAttachNote(data.row_count ? `Attached ${data.row_count} rows from ${file.name}` : "");
        textareaRef.current?.focus();
      } catch {
        setAttachNote("Couldn't read that spreadsheet. Please try again.");
      }
      return;
    }

    if (!isText) {
      if (file.type.startsWith("image/")) {
        setAttachNote("Images aren't supported yet — try a .xlsx, .csv, .txt, .md or .json file.");
      } else {
        setAttachNote("Unsupported file. Attach a .xlsx, .csv, .txt, .md or .json document.");
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      let content = String(reader.result || "");
      if (content.length > MAX_ATTACH_CHARS) {
        content = content.slice(0, MAX_ATTACH_CHARS) + "\n…[truncated]";
      }
      setAttachment({ name: file.name, content });
      setAttachNote("");
      textareaRef.current?.focus();
    };
    reader.onerror = () => setAttachNote("Couldn't read that file. Please try again.");
    reader.readAsText(file);
  };

  const removeAttachment = () => setAttachment(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    setVoiceError("");
    try {
      const form = new FormData();
      form.append("file", blob, "recording.webm");
      const res = await fetch(`${API_BASE}/transcribe`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: form,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Transcription failed (${res.status})`);
      }
      const data = await res.json();
      const text = (data.text || "").trim();
      if (text) {
        if (autoSendRef.current && !disabled) {
          // Hands-free: send straight away, no extra clicks.
          onSend(buildPayload(text));
          setInput("");
          setAttachment(null);
        } else {
          // Fallback (e.g. auto-send unavailable): drop the transcript in the box.
          setInput((prev) => (prev ? prev + " " : "") + text);
          if (textareaRef.current) {
            const ta = textareaRef.current;
            ta.focus();
            ta.style.height = "auto";
            ta.style.height = Math.min(ta.scrollHeight, 150) + "px";
          }
        }
      } else {
        setVoiceError("Didn't catch that — try again.");
      }
      autoSendRef.current = false;
    } catch (err) {
      setVoiceError(err.message || "Transcription error");
    } finally {
      setTranscribing(false);
    }
  };

  // Watch the mic level and auto-stop after a short pause — the "hands-free" behaviour:
  // tap once, speak, and it ends + sends itself when you stop talking.
  const monitorSilence = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);

      const SILENCE_RMS = 0.014;   // below this = silence
      const SILENCE_MS = 1500;     // stop after this much continuous silence
      const MIN_SPEAK_MS = 500;    // ignore the first moment (avoid instant cutoff)
      const startedAt = performance.now();
      let silenceStart = null;
      let heardSpeech = false;

      const tick = () => {
        if (!audioCtxRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const now = performance.now();
        if (rms >= SILENCE_RMS) {
          heardSpeech = true;
          silenceStart = null;
        } else if (heardSpeech && now - startedAt > MIN_SPEAK_MS) {
          if (silenceStart === null) silenceStart = now;
          else if (now - silenceStart > SILENCE_MS) {
            stopRecording();
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (_) {
      /* silence detection is best-effort; manual stop still works */
    }
  };

  const stopMonitor = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
  };

  const startRecording = async () => {
    setVoiceError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      autoSendRef.current = true; // voice-initiated → auto-send the transcript
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stopMonitor();
        stopStream();
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) transcribe(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      monitorSilence(stream); // hands-free auto-stop
    } catch (err) {
      setVoiceError("Microphone access denied.");
      stopStream();
    }
  };

  const stopRecording = () => {
    setRecording(false);
    stopMonitor();
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    } else {
      stopStream();
    }
  };

  // Clean up audio graph if the component unmounts mid-recording.
  useEffect(() => stopMonitor, []);

  const toggleMic = () => {
    if (disabled || transcribing) return;
    if (recording) stopRecording();
    else startRecording();
  };

  return (
    <form className="input-bar" onSubmit={handleSubmit}>
      {showQuickActions && (
        <div className="quick-chips">
          {QUICK_ACTIONS.map((action, i) => (
            <button
              key={i}
              type="button"
              className="quick-chip"
              onClick={() => onSend && onSend(action)}
              disabled={disabled}
            >
              {action}
            </button>
          ))}
        </div>
      )}

      {voiceError && <div className="voice-error">{voiceError}</div>}
      {attachNote && <div className="attach-note">{attachNote}</div>}

      {recording && (
        <div className="listening-pill" aria-live="polite">
          <span className="listening-dot" />
          Listening…
        </div>
      )}

      {attachment && (
        <div className="attach-chip" title={attachment.name}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
              stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          </svg>
          <span className="attach-chip-name">{attachment.name}</span>
          <button
            type="button"
            className="attach-chip-remove"
            onClick={removeAttachment}
            aria-label="Remove attachment"
            title="Remove attachment"
          >
            ×
          </button>
        </div>
      )}

      <div className={`input-wrapper${recording ? " is-recording" : ""}`}>
        {/* Hidden native file input backing the paperclip button */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.md,.csv,.json,.xlsx,.xls,text/plain,text/markdown,text/csv,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          style={{ display: "none" }}
          tabIndex={-1}
        />

        <button
          type="button"
          className="attach-btn"
          onClick={openFilePicker}
          disabled={disabled || recording || transcribing}
          aria-label="Attach a document"
          title="Attach a document (.txt, .md, .csv, .json)"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
            <path
              d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3 3 0 1 1 4.24 4.24l-8.49 8.49a1 1 0 0 1-1.41-1.41l7.78-7.78"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <textarea
          ref={textareaRef}
          className="input-textarea"
          value={input}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Ask Siragu anything…"}
          rows={1}
          disabled={disabled || recording || transcribing}
        />

        {VOICE_SUPPORTED && onOpenVoice && (
          <button
            type="button"
            className="voice-mode-btn"
            onClick={() => !disabled && !recording && !transcribing && onOpenVoice()}
            disabled={disabled || recording || transcribing}
            aria-label="Start voice conversation"
            title="Voice conversation (hands-free)"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 10v4M8 6v12M12 3v18M16 6v12M20 10v4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {VOICE_SUPPORTED && (
          <button
            type="button"
            className={`mic-btn${recording ? " recording" : ""}`}
            onClick={toggleMic}
            disabled={disabled || transcribing}
            aria-label={recording ? "Stop recording" : "Start voice input"}
            title={recording ? "Stop recording" : "Speak your question"}
          >
            {transcribing ? (
              <span className="mic-spinner" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 11v1a6 6 0 0 0 12 0v-1M12 18v3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}

        <button
          type="submit"
          className="send-btn"
          disabled={disabled || !input.trim()}
          aria-label="Send message"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 11L12 6L17 11M12 18V7"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="input-kb-hint">
        Need to add websites or large documents as permanent knowledge?{" "}
        <button
          type="button"
          className="kb-link"
          onClick={() => onOpenSettings && onOpenSettings()}
        >
          Settings → Knowledge Base
        </button>
      </div>
    </form>
  );
}

export default InputBar;
